import * as React from "react";
import {
    Menu,
    MenuTrigger,
    MenuPopover,
    Button,
    Input,
    makeStyles,
    tokens,
    shorthands,
    mergeClasses,
} from "@fluentui/react-components";
import { Search20Regular } from "@fluentui/react-icons";
import { BRAND } from "./risikoanalyseStep/tokens";

const useStyles = makeStyles({
    container: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        cursor: "pointer",
        ":hover .filter-icon": {
            visibility: "visible",
        },
    },
    filterIcon: {
        visibility: "hidden", // Hidden by default, shown on hover or if active
        color: tokens.colorNeutralForeground3,
    },
    filterIconActive: {
        visibility: "visible",
        color: tokens.colorBrandForeground1,
    },
    popoverContent: {
        ...shorthands.padding("10px"),
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        minWidth: "200px",
    },
    footerRow: {
        display: "flex",
        justifyContent: "space-between",
        marginTop: "8px",
    },
    boFoPanel: {
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        maxHeight: "280px",
        overflowY: "auto",
        minWidth: "240px",
    },
    boFoSection: { display: "flex", flexDirection: "column", gap: "4px" },
    sectionTitle: {
        fontWeight: 700,
        fontSize: "12px",
        color: BRAND.purple,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
    },
    checkRow: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        fontSize: "13px",
        color: BRAND.ink,
        cursor: "pointer",
    },
    searchInput: { marginBottom: "4px" },
});

export interface BoFoOption {
    id: string;
    label: string;
}

interface ColumnFilterProps {
    label: string;
    type?: "text" | "dropdown" | "boolean" | "boFo";
    options?: string[]; // for dropdown
    value?: string | boolean;
    onFilterChange: (val: string | boolean | undefined) => void;
    // --- only for type="boFo" ---
    /** Backoffice teams (from rbac/teams-lite). */
    boOptions?: BoFoOption[];
    /** Frontoffice users (from /users, mojemai excluded by the caller). */
    foOptions?: BoFoOption[];
    boSelected?: string[];
    foSelected?: string[];
    onBoFoChange?: (next: { boTeamIds: string[]; foUserIds: string[] }) => void;
}

export const ColumnFilter: React.FC<ColumnFilterProps> = ({
    label,
    type = "text",
    options,
    value,
    onFilterChange,
    boOptions,
    foOptions,
    boSelected,
    foSelected,
    onBoFoChange,
}) => {
    const classes = useStyles();
    const [inputValue, setInputValue] = React.useState(typeof value === "string" ? value : "");
    // Controlled menu so the footer buttons can close the popover (an
    // uncontrolled Fluent Menu only closes on outside-click / MenuItem).
    const [open, setOpen] = React.useState(false);
    const [boFoSearch, setBoFoSearch] = React.useState("");

    // Update local input state when prop changes
    React.useEffect(() => {
        if (typeof value === "string") setInputValue(value);
    }, [value]);

    const boSel = boSelected ?? [];
    const foSel = foSelected ?? [];

    const isActive =
        type === "boFo"
            ? boSel.length + foSel.length > 0
            : value !== undefined && value !== "" && value !== false;

    const handleApply = () => {
        onFilterChange(inputValue);
    };

    const handleClear = () => {
        setInputValue("");
        onFilterChange(undefined);
    };

    const toggleBo = (id: string) => {
        const next = boSel.includes(id) ? boSel.filter((x) => x !== id) : [...boSel, id];
        onBoFoChange?.({ boTeamIds: next, foUserIds: foSel });
    };
    const toggleFo = (id: string) => {
        const next = foSel.includes(id) ? foSel.filter((x) => x !== id) : [...foSel, id];
        onBoFoChange?.({ boTeamIds: boSel, foUserIds: next });
    };

    const needle = boFoSearch.trim().toLowerCase();
    const filterOpts = (opts?: BoFoOption[]) =>
        (opts ?? []).filter((o) => !needle || o.label.toLowerCase().includes(needle));

    return (
        <div className={classes.container}>
            <span>{label}</span>
            <Menu open={open} onOpenChange={(_, data) => setOpen(data.open)}>
                <MenuTrigger disableButtonEnhancement>
                    <Button
                        appearance="subtle"
                        icon={<Search20Regular />} // Lupe = „filtern" (wie in der DataTable-Engine); aktiv eingefärbt
                        className={
                            isActive
                                ? classes.filterIconActive
                                : mergeClasses("filter-icon", classes.filterIcon)
                        }
                        size="small"
                    />
                </MenuTrigger>
                <MenuPopover>
                    <div className={classes.popoverContent}>
                        {type === "text" && (
                            <Input
                                placeholder={`Filter ${label}...`}
                                value={inputValue}
                                onChange={(e, data) => setInputValue(data.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleApply();
                                        setOpen(false);
                                    }
                                }}
                            />
                        )}
                        {type === "dropdown" && options && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {options.map(opt => (
                                    <Button
                                        key={opt}
                                        appearance={opt === value ? "primary" : "subtle"}
                                        onClick={() => { onFilterChange(opt); setOpen(false); }}
                                        style={{ justifyContent: 'flex-start' }}
                                    >
                                        {opt}
                                    </Button>
                                ))}
                            </div>
                        )}
                        {type === "boolean" && (
                            <Button
                                appearance={value === true ? "primary" : "outline"}
                                onClick={() => onFilterChange(value === true ? undefined : true)}
                            >
                                {label}
                            </Button>
                        )}

                        {type === "boFo" && (
                            <div className={classes.boFoPanel}>
                                <Input
                                    className={classes.searchInput}
                                    placeholder="Suchen…"
                                    value={boFoSearch}
                                    onChange={(_e, data) => setBoFoSearch(data.value)}
                                    size="small"
                                />
                                <div className={classes.boFoSection}>
                                    <span className={classes.sectionTitle}>Backoffice (Team)</span>
                                    {filterOpts(boOptions).map((o) => (
                                        <label key={o.id} className={classes.checkRow}>
                                            <input
                                                type="checkbox"
                                                checked={boSel.includes(o.id)}
                                                onChange={() => toggleBo(o.id)}
                                            />
                                            {o.label}
                                        </label>
                                    ))}
                                </div>
                                <div className={classes.boFoSection}>
                                    <span className={classes.sectionTitle}>Frontoffice (Person)</span>
                                    {filterOpts(foOptions).map((o) => (
                                        <label key={o.id} className={classes.checkRow}>
                                            <input
                                                type="checkbox"
                                                checked={foSel.includes(o.id)}
                                                onChange={() => toggleFo(o.id)}
                                            />
                                            {o.label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        {type === "text" && (
                            <div className={classes.footerRow}>
                                <Button size="small" appearance="outline" onClick={() => { handleClear(); setOpen(false); }}>Reset</Button>
                                <Button size="small" appearance="primary" onClick={() => { handleApply(); setOpen(false); }}>Filtern</Button>
                            </div>
                        )}
                        {type === "boFo" && (
                            <div className={classes.footerRow}>
                                <Button size="small" appearance="outline" onClick={() => { onBoFoChange?.({ boTeamIds: [], foUserIds: [] }); setOpen(false); }}>Reset</Button>
                                <Button size="small" appearance="primary" onClick={() => setOpen(false)}>Filtern</Button>
                            </div>
                        )}
                        {type !== "text" && type !== "boFo" && isActive && (
                            <Button size="small" appearance="subtle" onClick={() => onFilterChange(undefined)}>Filter zurücksetzen</Button>
                        )}
                    </div>
                </MenuPopover>
            </Menu>
        </div>
    );
};
